<script setup lang="ts">
import { computed } from 'vue';

import { EntityPresenterMode } from '@satellite/composables/presenterEntityMode';
import { useYardAreaPresenter } from '@satellite/composables/useYardAreaPresenter';
import { useYardSpotFetcher } from '@satellite/composables/useYardSpotFetcher';
import { useYardSpotDisplay } from '@satellite/composables/useYardSpotDisplay';
import YardSpotDisplay from '@satellite/components/elements/presenters/YardSpotDisplay.vue';

import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterPlaceholder';
import type { YardAreaDetails } from '@satellite/composables/useYardAreaPresenter';
import type { YardSpotDetails } from '@satellite/composables/useYardSpotPresenter';
import type { YardSpotDisplayField } from '@satellite/composables/useYardSpotDisplay';

const {
  size = 'regular',
  spotId = null,
  initialSpotData = null,
  placeholderSpotData = null,
  staticSpotData = null,
  initialAreaData = null,
  placeholderAreaData = null,
  staticAreaData = null,
  mode = EntityPresenterMode.Live,
  displayFields = ['area', 'code', 'spotIcon'],
  areaProps = {},
  spotCodeProps = {},
  spotIconProps = {},
  spotStatusProps = {}
} = defineProps<{
  size?: 'regular' | 'small' | 'large';
  spotId?: Maybe<string>;
  initialSpotData?: Maybe<YardSpotDetails>;
  placeholderSpotData?: Maybe<PresenterPlaceholderData<YardSpotDetails>>;
  staticSpotData?: Maybe<YardSpotDetails>;
  initialAreaData?: Maybe<YardAreaDetails>;
  placeholderAreaData?: Maybe<PresenterPlaceholderData<YardAreaDetails>>;
  staticAreaData?: Maybe<YardAreaDetails>;
  mode?: EntityPresenterMode;
  displayFields?: YardSpotDisplayField[];
  areaProps?: Record<string, unknown>;
  spotCodeProps?: Record<string, unknown>;
  spotIconProps?: Record<string, unknown>;
  spotStatusProps?: Record<string, unknown>;
}>();

const needsArea = computed(() => displayFields.includes('area'));

const {
  data: spot,
  isLoading,
  isFetching,
  onIntersect: onSpotIntersect
} = useYardSpotFetcher({
  spotId: computed(() => spotId),
  initialSpotData: computed(() => initialSpotData),
  placeholderSpotData: computed(() => placeholderSpotData),
  staticSpotData: computed(() => staticSpotData),
  mode
});

const areaId = computed(() => (needsArea.value ? (spot.value?.spotAreaId ?? null) : null));

const {
  data: area,
  isFetching: isAreaFetching,
  onIntersect: onAreaIntersect
} = useYardAreaPresenter({
  areaId,
  initialAreaData: computed(() => initialAreaData),
  placeholderAreaData: computed(() => placeholderAreaData),
  staticAreaData: computed(() => staticAreaData),
  mode
});

const onIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
  onSpotIntersect(entry);
  if (needsArea.value) {
    onAreaIntersect(entry);
  }
};

const data = useYardSpotDisplay(spot, {
  area,
  size: computed(() => size),
  mode: computed(() => mode),
  isFetching,
  isAreaFetching,
  initialAreaData: computed(() => initialAreaData),
  placeholderAreaData: computed(() => placeholderAreaData),
  staticAreaData: computed(() => staticAreaData),
  areaProps: computed(() => areaProps),
  codeProps: computed(() => spotCodeProps),
  spotIconProps: computed(() => spotIconProps),
  spotStatusProps: computed(() => spotStatusProps)
});
</script>

<template>
  <div
    v-intersect="{
      handler: onIntersect,
      options: {
        threshold: [0.5]
      }
    }">
    <v-skeleton-loader
      :loading="isLoading"
      type="text"
      class="yard-spot-presenter-v2 bg-transparent">
      <yard-spot-display
        :data="data"
        :display-fields="displayFields"
        :style="isLoading ? 'min-width: 50px' : ''">
        <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
          <slot :name="slotName" v-bind="slotProps ?? {}" />
        </template>
      </yard-spot-display>
    </v-skeleton-loader>
  </div>
</template>

<style scoped>
:deep(.v-skeleton-loader__text) {
  margin: 0 auto !important;
  height: 1em !important;
}
</style>
